import React from 'react';
import FeatureAnnouncementModal from '../FeatureAnnouncementModal';

// Metric Card Component
const MetricCard = ({ title, value, subtitle, icon, color = 'indigo', trend, href }) => {
  const colorClasses = {
    indigo: 'bg-indigo-50 text-indigo-600',
    green: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    blue: 'bg-sky-50 text-sky-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  const Content = (
    <div className={`relative overflow-hidden rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200 ${href ? 'hover:ring-indigo-300 hover:shadow-md transition-all cursor-pointer' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900">{value}</p>
          {subtitle && (
            <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
          )}
          {trend && (
            <div className={`mt-2 inline-flex items-center text-sm font-medium ${trend.positive ? 'text-emerald-600' : 'text-red-600'}`}>
              {trend.positive ? (
                <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              ) : (
                <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                </svg>
              )}
              {trend.value}
            </div>
          )}
        </div>
        <div className={`rounded-lg p-3 ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
      {href && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </div>
  );

  if (href) {
    return <a href={href} className="block group">{Content}</a>;
  }
  return Content;
};

// Action Item Component
const ActionItem = ({ title, description, count, urgency = 'normal', href, actionText }) => {
  const urgencyClasses = {
    high: 'border-l-red-500 bg-red-50',
    medium: 'border-l-amber-500 bg-amber-50',
    normal: 'border-l-blue-500 bg-blue-50',
  };

  const badgeClasses = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-amber-100 text-amber-700',
    normal: 'bg-blue-100 text-blue-700',
  };

  return (
    <a
      href={href}
      className={`block border-l-4 ${urgencyClasses[urgency]} rounded-r-lg p-4 hover:shadow-md transition-shadow`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-gray-900">{title}</h4>
            {count > 0 && (
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${badgeClasses[urgency]}`}>
                {count}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-600">{description}</p>
        </div>
        <svg className="h-5 w-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </a>
  );
};

// Quick Action Button
const QuickAction = ({ icon, label, href, description }) => (
  <a
    href={href}
    className="group flex flex-col items-center rounded-xl border border-gray-200 bg-white p-6 text-center hover:border-indigo-300 hover:shadow-lg transition-all"
  >
    <div className="rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 p-3 text-white shadow-lg group-hover:scale-110 transition-transform">
      {icon}
    </div>
    <h3 className="mt-4 font-semibold text-gray-900">{label}</h3>
    <p className="mt-1 text-sm text-gray-500">{description}</p>
  </a>
);

// Recent Activity Item
const ActivityItem = ({ type, title, subtitle, timestamp, amount, status }) => {
  const icons = {
    transaction: (
      <div className="rounded-full bg-emerald-100 p-2">
        <svg className="h-4 w-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    ),
    member: (
      <div className="rounded-full bg-blue-100 p-2">
        <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
        </svg>
      </div>
    ),
    invoice: (
      <div className="rounded-full bg-purple-100 p-2">
        <svg className="h-4 w-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
    ),
  };

  const statusBadges = {
    paid: 'bg-emerald-100 text-emerald-700',
    pending: 'bg-amber-100 text-amber-700',
    failed: 'bg-red-100 text-red-700',
  };

  return (
    <div className="flex items-center gap-4 py-3">
      {icons[type]}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
        <p className="text-sm text-gray-500 truncate">{subtitle}</p>
      </div>
      <div className="text-right flex-shrink-0">
        {amount && <p className="text-sm font-semibold text-gray-900">{amount}</p>}
        {status && (
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusBadges[status]}`}>
            {status}
          </span>
        )}
        <p className="text-xs text-gray-400">{timestamp}</p>
      </div>
    </div>
  );
};

const fundBadge = (type) => {
  if (type === 'invoice') {
    return { label: 'Invoicing', className: 'bg-purple-100 text-purple-700' };
  }
  if (type === 'dues' || type === 'misc_dues') {
    return { label: 'Dues', className: 'bg-blue-100 text-blue-700' };
  }
  return { label: 'Donation', className: 'bg-teal-100 text-teal-700' };
};

// Fund Summary Card
const FundSummaryCard = ({ fund, path }) => {
  const hasOverdue = fund.overdueCount > 0;
  const badge = fundBadge(fund.type);
  const showMembers = fund.type === 'invoice' || fund.type === 'dues' || fund.type === 'misc_dues';

  return (
    <a
      href={path}
      className="block rounded-xl border border-gray-200 bg-white p-5 hover:border-indigo-300 hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between">
        <div>
          <h4 className="font-semibold text-gray-900">{fund.name}</h4>
          {showMembers && (
            <p className="mt-1 text-sm text-gray-500">{fund.memberCount} members</p>
          )}
        </div>
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${badge.className}`}>
          {badge.label}
        </span>
      </div>

      {fund.type === 'invoice' && (
        <div className="mt-4 flex items-center gap-4 text-sm">
          {hasOverdue ? (
            <div className="flex items-center text-red-600">
              <svg className="mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {fund.overdueCount} overdue
            </div>
          ) : (
            <div className="flex items-center text-emerald-600">
              <svg className="mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              All current
            </div>
          )}
          {fund.nextDueDate && (
            <span className="text-gray-500">
              Next due: {fund.nextDueDate}
            </span>
          )}
        </div>
      )}

      {fund.type !== 'invoice' && fund.recentTransactions != null && (
        <div className="mt-4 text-sm text-gray-500">
          {fund.recentTransactions} transactions in last 30 days
        </div>
      )}
    </a>
  );
};

// Main Dashboard Component
const AdminDashboard = ({
  organization,
  metrics,
  actionItems,
  funds,
  recentActivity,
  quickActionUrls,
  pendingApplicationsCount,
  pendingApplicationsUrl,
  showSubmissionGroupsAnnouncement,
  dismissFeatureUrl,
  hasDuesPro,
  planUrl,
  submissionGroupsUrl,
  featureAnnouncementImageHeroUrl,
  featureAnnouncementImageDashboardUrl,
  featureAnnouncementImageWorkflowUrl,
  featureAnnouncementVideoUrl,
  featureAnnouncementKey,
}) => {
  const hasActionItems = actionItems && actionItems.length > 0;
  const hasOverdueMembers = metrics.overdueMembers > 0;
  const hasPendingApplications = pendingApplicationsCount > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      {showSubmissionGroupsAnnouncement && (
        <FeatureAnnouncementModal
          showSubmissionGroupsAnnouncement={showSubmissionGroupsAnnouncement}
          dismissFeatureUrl={dismissFeatureUrl}
          submissionGroupsUrl={submissionGroupsUrl}
          planUrl={planUrl}
          hasDuesPro={hasDuesPro}
          featureAnnouncementImageHeroUrl={featureAnnouncementImageHeroUrl}
          featureAnnouncementImageDashboardUrl={featureAnnouncementImageDashboardUrl}
          featureAnnouncementImageWorkflowUrl={featureAnnouncementImageWorkflowUrl}
          featureAnnouncementVideoUrl={featureAnnouncementVideoUrl}
          featureAnnouncementKey={featureAnnouncementKey}
        />
      )}
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                Welcome back!
              </h1>
              <p className="mt-1 text-gray-500">
                Here's what's happening with <span className="font-medium text-gray-700">{organization.name}</span> today.
              </p>
            </div>
            <div className="flex gap-3">
              <a
                href={quickActionUrls.editOrganization}
                className="inline-flex items-center rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50 transition-colors"
              >
                <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </a>
              <a
                href={quickActionUrls.landingPage}
                className="inline-flex items-center rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:from-indigo-700 hover:to-purple-700 transition-colors"
              >
                <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                View Public Page
              </a>
            </div>
          </div>
        </div>

        {/* Alert Banner for Critical Issues */}
        {hasOverdueMembers && (
          <div className="mb-8 rounded-xl bg-gradient-to-r from-red-500 to-rose-600 p-4 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-white/20 p-2">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-white">Attention Required</h3>
                <p className="text-red-100">
                  You have {metrics.overdueMembers} {metrics.overdueMembers === 1 ? 'member' : 'members'} with overdue invoices totaling {metrics.overdueAmount}.
                </p>
              </div>
              <a
                href={quickActionUrls.overdueMembers}
                className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
              >
                View All
              </a>
            </div>
          </div>
        )}

        {/* Pending Applications Banner */}
        {hasPendingApplications && (
          <a
            href={pendingApplicationsUrl}
            className="mb-8 flex items-center gap-4 rounded-xl bg-amber-50 border border-amber-200 p-4 hover:bg-amber-100 transition-colors group"
          >
            <div className="rounded-full bg-amber-100 p-2 flex-shrink-0">
              <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800">
                {pendingApplicationsCount} membership {pendingApplicationsCount === 1 ? 'application needs' : 'applications need'} to be reviewed
              </h3>
              <p className="text-sm text-amber-600">Click to view pending applications</p>
            </div>
            <svg className="h-5 w-5 text-amber-400 group-hover:text-amber-600 transition-colors flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        )}

        {/* Key Metrics Grid */}
        <div className="mb-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Total Members"
            value={metrics.totalMembers.toLocaleString()}
            subtitle={metrics.newMembersThisMonth > 0 ? `+${metrics.newMembersThisMonth} this month` : null}
            color="blue"
            href={quickActionUrls.allMembers}
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            }
          />

          <MetricCard
            title="Revenue This Month"
            value={metrics.revenueThisMonth}
            subtitle={`${metrics.transactionsThisMonth} transactions`}
            color="green"
            href={quickActionUrls.transactions}
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />

          <MetricCard
            title="Collection Rate"
            value={`${metrics.collectionRate}%`}
            subtitle="Invoices paid on time"
            color={metrics.collectionRate >= 80 ? 'green' : metrics.collectionRate >= 60 ? 'amber' : 'red'}
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            }
          />

          <MetricCard
            title="Pending Invoices"
            value={metrics.pendingInvoices.toLocaleString()}
            subtitle={metrics.pendingAmount}
            color={metrics.pendingInvoices > 0 ? 'amber' : 'green'}
            href={quickActionUrls.pendingInvoices}
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid gap-8 lg:grid-cols-3">
          {/* Left Column - Quick Actions & Funds */}
          <div className="lg:col-span-2 space-y-8">
            {/* Quick Actions */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid gap-4 sm:grid-cols-3">
                <QuickAction
                  href={quickActionUrls.addMember}
                  label="Add Member"
                  description="Add a single member"
                  icon={
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                  }
                />
                <QuickAction
                  href={quickActionUrls.uploadMembers}
                  label="Upload Members"
                  description="Bulk import via spreadsheet"
                  icon={
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                  }
                />
                <QuickAction
                  href={quickActionUrls.sendReminder}
                  label="Send Reminder"
                  description="Email members about dues"
                  icon={
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  }
                />
              </div>
            </div>

            {/* Your Funds */}
            {funds && funds.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Your Funds</h2>
                  <a href={quickActionUrls.allFunds} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                    View all →
                  </a>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  {funds.slice(0, 4).map((fund) => (
                    <FundSummaryCard key={fund.id} fund={fund} path={fund.path} />
                  ))}
                </div>
              </div>
            )}

            {/* Empty State - No Funds */}
            {(!funds || funds.length === 0) && (
              <div className="rounded-xl border-2 border-dashed border-gray-300 bg-white p-12 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <h3 className="mt-4 text-lg font-semibold text-gray-900">No funds yet</h3>
                <p className="mt-2 text-gray-500">Get started by creating your first fund to collect dues from members.</p>
                <div className="mt-6 flex justify-center gap-3">
                  <a
                    href={quickActionUrls.createFund}
                    className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                  >
                    <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Fund
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Actions & Activity */}
          <div className="space-y-8">
            {/* Action Items */}
            {hasActionItems && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Needs Attention</h2>
                <div className="space-y-3">
                  {actionItems.map((item, index) => (
                    <ActionItem
                      key={index}
                      title={item.title}
                      description={item.description}
                      count={item.count}
                      urgency={item.urgency}
                      href={item.href}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Recent Activity */}
            {recentActivity && recentActivity.length > 0 && (
              <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
                  <a href={quickActionUrls.transactions} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                    View all
                  </a>
                </div>
                <div className="divide-y divide-gray-100">
                  {recentActivity.map((activity, index) => (
                    <ActivityItem
                      key={index}
                      type={activity.type}
                      title={activity.title}
                      subtitle={activity.subtitle}
                      timestamp={activity.timestamp}
                      amount={activity.amount}
                      status={activity.status}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Help Card */}
            <div className="rounded-xl bg-gradient-to-br from-indigo-600 to-purple-700 p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-white/20 p-2">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold">Need Help?</h3>
                  <p className="mt-1 text-sm text-indigo-100">
                    Check out our guides or reach out to support.
                  </p>
                  <a
                    href="mailto:support@unionly.com"
                    className="mt-3 inline-flex items-center text-sm font-medium text-white hover:text-indigo-100"
                  >
                    Contact Support
                    <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

